import * as React from 'react'
import { ReportType } from '@/app/constants/reportType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getReportNameByType } from '@/app/services/printout/reportTypeResolver'
import { faClose, FaIcon } from '@/lib/fa'

import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import PrintoutParamsRenderer from '../renders/PrintoutParamsRenderer'
import { useRaportForm } from '../context/RaportFormContext'
import { usePrintJobRunning } from '../stores/printJobStore'
import { Portal } from '@/ui/overlays/Portal'
import { Spinner } from '@/ui/feedback'
import CheckboxWithLabelField from '@/modules/drivers/forms/contract/inputs/CheckboxWithLabelField'
import { DatePickerRangeProps } from '@/modules/drivers/types'

type Props = {
  isOpen: boolean
  selectedReport: ReportType | null
  onClose: () => void
  onSubmit: (params: any) => void
  translate: (text: string) => string
  companyId: string
  pending?: boolean
  dateRange?: DatePickerRangeProps
}

const PrintoutParamsModal: React.FC<Props> = ({
  isOpen,
  selectedReport,
  onClose,
  onSubmit,
  translate,
  companyId,
  pending,
  dateRange,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const isGenerating = usePrintJobRunning()
  const { data } = useRaportForm()
  const [issuerSignature, setIssuerSignature] = React.useState(true)
  const [driverSignature, setDriverSignature] = React.useState(true)
  const printoutRendererRef = React.useRef<any>(null)

  const isBusy = Boolean(pending) || isGenerating || loading

  if (!isOpen || !selectedReport || selectedReport === ReportType.NONE) {
    return null
  }

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (isBusy) return

    onSubmit({ ...data, hideSignatures: false, issuerSignature, driverSignature })
  }

  return (
    <Portal layer="modal">
    <div className="tw-fixed tw-inset-0 tw-z-[999] tw-flex tw-items-center tw-justify-center tw-overflow-auto tw-overflow-y-auto tw-bg-content-base tw-bg-opacity-70">
      <form
        onSubmit={handleSubmit}
        className="tw-relative tw-z-[10000] tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[800px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
            {t(getReportNameByType(selectedReport))}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => onClose()}
          />
        </div>

        <div className="tw-overflow-y-auto">
          <PrintoutParamsRenderer
            companyId={companyId}
            ref={(connectedComponent) => {
              if (connectedComponent != null) {
                printoutRendererRef.current = connectedComponent
              }
            }}
            selectedReport={selectedReport}
            translate={translate}
            dateRange={dateRange}
          />
          <div className="tw-px-4 tw-pb-4 tw-pr-8">
            <CheckboxWithLabelField
              value={issuerSignature}
              onChange={setIssuerSignature}
              label={t('printout.issuer-signature')}
              disabled={isBusy}
            />
            <CheckboxWithLabelField
              value={driverSignature}
              onChange={setDriverSignature}
              label={t('printout.driver-signature')}
              disabled={isBusy}
            />
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-base-300">
          {isBusy && (
            <span className="tw-text-xs tw-text-content-base/70">
              {t('printout.processing-in-progress')}
            </span>
          )}
          <button
            disabled={isBusy}
            type="submit"
            className={`tw-m-4 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:hover:tw-opacity-60`}
          >
            {isBusy && <Spinner className="tw-h-4 tw-w-4" />}
            {t('common.confirm')}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}

export default PrintoutParamsModal
